import React, { useState, useEffect, useContext } from 'react';
import { Column } from 'react-table';
import { KTIcon } from '../../../_myb/helpers/components/KTIcon';
import { DataTable } from '../widgets/components/DataTable';
import { Content } from '../../../_myb/layout/components/content';
import { deleteTax, fetchAccounts, fetchTax, fetchTaxes, upsertTax } from '../../api';
import { Account, SystemAccountCode, Tax } from '../../types';
import { ToolbarContext, defaultToolbarState } from '../../../_myb/layout/components/toolbar/Toolbar';
import { CustomPrompt } from '../widgets/components/ModalPrompts';
import CustomModal from '../widgets/components/CustomModal';
import AccountForm from '../masterdata/components/AccountForm';
import NumberInput from '../widgets/components/NumberInput';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { generateShortUUID } from '../misc_objects/Utils';
import SliderSwitch from '../widgets/components/SliderSwitch';
import { TaxPicker } from '../masterdata/components/TaxPicker';
import { AccountPicker } from '../masterdata/components/AccountPicker';
import { useAppContext } from '../apps/AppContext';
import { AccountCategory } from '../../Constants';
import { Button } from 'react-bootstrap';
import TaxSettingsButton from '../masterdata/components/TaxSettingsButton';


export const GSTTreatments = [
  { value: 'Consumer', label: 'Consumer' },
  { value: 'DeemedExports', label: 'Deemed Exports' },
  { value: 'Ecommerce', label: 'Ecommerce' },
  { value: 'GSTRegisteredRegular', label: 'GST Registered - Regular' },
  { value: 'GSTRegisteredComposition', label: 'GST Registered - Composition' },
  { value: 'GSTUnregistered', label: 'GST unregistered' },
  { value: 'UINHolders', label: 'UIN Holders' },
  { value: 'SEZ', label: 'SEZ' },
  { value: 'Overseas', label: 'Overseas' },
];




export const TaxSettings: React.FC = () => {
  const { updateToolbar } = useContext(ToolbarContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [taxForEdit, setTaxForEdit] = useState<Tax>()
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const [taxIdToDelete, setTaxIdToDelete] = useState<string | null>(null);

  const handleDeleteTax = (taxId: string) => {
    setTaxIdToDelete(taxId);
  };

  const confirmDeleteTax = async () => {
    const taxId = taxIdToDelete;
    setTaxIdToDelete(null);
    if (taxId) {
      try {
        await deleteTax(taxId);
        toast.success('Tax deleted successfully!'.toTranslatedDisplay());
        // Refresh the tax list
        const result = await fetchTaxes();
        setTaxes(result);
      } catch (error: any) {
        // Better error logging
        console.error('Delete error:', error);
        console.error('Error message:', error.message);
        console.error('Error response:', error.response?.data);

        // Display error message
        const errorMessage = error.message || error.response?.data?.message || 'Failed to delete tax'.toTranslatedDisplay();
        toast.error(errorMessage);
      }
    }
  }
  const updateButtons = () => {
    updateToolbar({
      ...defaultToolbarState,
      primaryButton: {
        enabled: true,
        text: 'Create Tax',
        icon: 'fa-solid fa-plus', // Replace with appropriate icon type
        action: async () => {
          setTaxForEdit(new Tax())
          setIsModalOpen(true);
        }
      },
    });
  };

  const handleSave = (data: Tax) => {
    // Add your save logic here
    setIsModalOpen(false);
  };

  const [taxes, setTaxes] = useState<Tax[]>([]);

  useEffect(() => {
    updateButtons()
    const fetchData = async () => {
      const result = await fetchTaxes();
      setTaxes(result);
    };
    fetchData();
  }, [isModalOpen]);

  const columns: Column<Tax>[] = React.useMemo(
    () => [
      {
        Header: 'Tax Name'.toTranslatedDisplay(),
        accessor: 'taxName',
        ClassName: ''
      },
      {
        Header: 'Tax Type'.toTranslatedDisplay(),
        accessor: 'taxType',
        ClassName: ''
      },
      {
        Header: 'Tax Pct%'.toTranslatedDisplay(),
        accessor: 'taxPct',
        ClassName: 'n2'
      },
      {
        Header: 'Actions'.toTranslatedDisplay(),
        accessor: 'taxId', // Use a valid field from your data
        disableSortBy: true,
        width: '15%',
        ClassName: 'text-center',
        Cell: ({ row }: { row: { original: Tax } }) => (
          <div>
            <button onClick={() => { handleEditTax(row.original.taxId) }} className="btn btn-sm btn-light p-2 m-1" data-bs-toggle="tooltip" data-bs-placement="top" title={'Edit'.toTranslatedDisplay()}>
              <KTIcon className="fs-3 text-hover-primary text-muted" iconType='solid' iconName='pencil' />
            </button>
            <button onClick={() => { handleDeleteTax(row.original.taxId) }} className="btn btn-sm btn-light p-2 m-1" data-bs-toggle="tooltip" data-bs-placement="top" title={'Delete'.toTranslatedDisplay()}>
              <KTIcon className="fs-3 text-muted text-hover-danger" iconName='trash' />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const handleEditTax = async (taxId: string) => {
    const tax = await fetchTax(taxId)
    setTaxForEdit(tax)
    setIsModalOpen(true)
  }

  return (
    <Content>
      <DataTable columns={columns} data={taxes} headerToolbar={
        <>
          <div className='mt-1'>
            <TaxSettingsButton onUpdate={() => { }} />
          </div>

        </>
      }
        emptyStateActions={[
          { label: 'Create Tax'.toTranslatedDisplay(), onClick: () => { setTaxForEdit(new Tax()); setIsModalOpen(true); } },
        ]} />
      <CustomModal
        title={'Add Tax'.toTranslatedDisplay()}
        isVisible={isModalOpen}
        onClose={handleModalClose}
        body={<TaxForm tax={taxForEdit} onClose={() => { setIsModalOpen(false) }} />}
        size='medium'
        id="accountModal"
        onSave={() => {
        }} // Pass an empty function since it's handled inside AccountForm
      />
      <CustomPrompt
        isVisible={!!taxIdToDelete}
        onClose={() => setTaxIdToDelete(null)}
        onConfirm={confirmDeleteTax}
        message={'Are you sure you want to delete this tax?'.toTranslatedDisplay()}
        title={'Confirm Deletion'.toTranslatedDisplay()}
        PrimaryText={'Delete'.toTranslatedDisplay()}
        secondaryText={'Cancel'.toTranslatedDisplay()}
      />
    </Content>
  );
};


interface TaxFormProps {
  tax?: Tax;
  onClose: () => void;
  onSave?: () => void;
}

export const TaxForm: React.FC<TaxFormProps> = ({ tax, onClose, onSave }) => {
  const { bizMaster } = useAppContext();
  const isCIN = (bizMaster?.bizDetails?.country === 'IN')
  const [triggerCalc, setTriggerCalc] = useState(false)
  const { register, handleSubmit, setValue, getValues, reset, control, watch, formState: { errors } } = useForm<Tax>({
    defaultValues: {
      taxId: tax?.taxId || generateShortUUID(),
      isTaxGroup: tax?.isTaxGroup,
      taxName: tax?.taxName || '',  // Initialize with an empty string instead of undefined
      taxPct: tax?.taxPct !== undefined ? tax.taxPct : 0,  // Initialize with 0 instead of undefined
      intraTax: tax?.intraTax,
      interTax: tax?.interTax
    }
  });

  const watchIsTaxGroup = watch('isTaxGroup');
  const watchTaxAcct = watch('taxAcct');
  const watchTaxPayableAcct = watch('taxPayableAcct');

  useEffect(() => {
    const initForm = async () => {
      if (tax) {
        reset({
          taxId: tax.taxId || generateShortUUID(),
          isTaxGroup: tax.isTaxGroup || false,
          taxName: tax.taxName || '',
          taxPct: tax.taxPct !== undefined ? tax.taxPct : 0,
          subTaxes: tax.subTaxes || [],
          intraTax: tax.intraTax,
          interTax: tax.interTax
        });

        if (!tax.isTaxGroup && (!tax.taxAcct || !tax.taxPayableAcct)) {
          try {
            const allAccounts = await fetchAccounts();
            if (!tax.taxAcct) {
              const steAccount = allAccounts.find(a => a.systemAccountCode === SystemAccountCode.STE);
              if (steAccount?.accountId) setValue('taxAcct', steAccount.accountId);
            }
            if (!tax.taxPayableAcct) {
              const stpAccount = allAccounts.find(a => a.systemAccountCode === SystemAccountCode.STP);
              if (stpAccount?.accountId) setValue('taxPayableAcct', stpAccount.accountId);
            }
          } catch {
            // user can select manually
          }
        }
      }
    };
    initForm();
  }, [tax, reset]);

  const onSubmit = async (data: Tax) => {
    if (!data.taxName || data.taxPct === undefined || data.taxPct === null) {
      console.error('Validation Error: Tax Name and Tax Percentage are required.');
      return;
    }
    try {
      const taxUpdateResponse = await upsertTax(data)
      toast.success('Tax saved successfully!'.toTranslatedDisplay())
      reset({ ...new Tax() })
      if (onSave) onSave()
      onClose();
    } catch (error: any) {
      toast.error(error.message)
    }

  };

  const updateTotalTaxPct = () => {
    const current = getValues('subTaxes') || [];
    const total = current.reduce(
      (sum, t) => sum + Number(t.taxPct || 0),
      0
    );
    setValue('taxPct', total);
  };

  // useFieldArray to manage subTaxes array
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "subTaxes",
  });
  useEffect(() => {
    if (tax?.isTaxGroup && tax.subTaxes?.length) {
      replace(tax.subTaxes);
    }
  }, [tax]);
  const subTaxes = useWatch({
  control,
  name: 'subTaxes',
});
  useEffect(() => {
    if (watchIsTaxGroup) {
      const totalPct =
        subTaxes?.reduce((acc, t) => acc + Number(t.taxPct || 0), 0) || 0;
      setValue('taxPct', totalPct);
    }
  }, [subTaxes, watchIsTaxGroup, setValue]);
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-2">
      <div className="mb-3">
        <label className="form-label">{'Tax Name'.toTranslatedDisplay()}</label>
        <input
          className="form-control"
          {...register('taxName', { required: 'Tax Name is required'.toTranslatedDisplay() })}
          placeholder={'Tax Name'.toTranslatedDisplay()}
        />
        {errors.taxName && <p className="text-danger">{errors.taxName.message}</p>}
      </div>

      <div className="mb-3">
        <label className="form-label">{'Tax Rate'.toTranslatedDisplay()}</label>
        <Controller
          name="taxPct"
          control={control}
          rules={{ required: 'Tax Percentage is required'.toTranslatedDisplay(), min: 0, max: 100 }}
          render={({ field }) => (
            <NumberInput
              {...field}
              placeholder={'Tax Percentage (%)'.toTranslatedDisplay()}
              inputDisabled={watchIsTaxGroup}
              value={field.value}  // Convert value to number  // Ensure value is controlled
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => field.onChange(event.target.value)}
            />
          )}
        />
        {errors.taxPct && <p className="text-danger">{errors.taxPct.message}</p>}
      </div>

      <div className="mb-3">
        <label className='form-label'>{'Tax Group?'.toTranslatedDisplay()}</label>
        <div className='d-flex align-items-center mt-1'>
          <Controller
            name="isTaxGroup"
            control={control}
            render={({ field }) => (
              <>
                <SliderSwitch
                  name={field.name}  // Pass the name of the field
                  defaultChecked={!!field.value}
                  onChange={(name, value) => {
                    field.onChange(value);

                    if (!value) {
                      replace([]);
                      setValue('taxPct', 0);
                    } else if (fields.length === 0) {
                      append({ taxName: '', taxPct: 0 });
                    }
                  }
                  }
                />
                <p className='mb-0 ms-2'>{field.value ? 'Group'.toTranslatedDisplay() : 'Tax Code'.toTranslatedDisplay()}</p>
              </>
            )}
          />
        </div>
      </div>
      {watchIsTaxGroup ? (
        <>
          <div>
            {fields.map((tax, index) => (
              <div key={tax.id} className='d-flex justify-items-center align-items-center mt-2'>
                <div className='w-50'>
                  <Controller
                    name={`subTaxes.${index}.taxName`}
                    control={control}
                    render={({ field }) => (
                      <TaxPicker
                        label={'Tax Name'.toTranslatedDisplay()}
                        {...field}
                        tabIndex={0}
                        selectedTax={tax.taxName}
                        onTaxChange={(taxId: string, taxPct: number) => {
                          field.onChange(taxId);
                          setValue(`subTaxes.${index}.taxPct`, taxPct);
                        }}
                        onTextInput={(value) => {
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </div>
                <div className='mx-2'></div>
                <Controller
                  name={`subTaxes.${index}.taxPct`}
                  control={control}
                  render={({ field }) => (
                    <NumberInput
                      {...field}
                      label={'Tax Pct'.toTranslatedDisplay()}
                      placeholder={'Tax Percentage (%)'.toTranslatedDisplay()}
                      value={field.value !== undefined ? Number(field.value) : 0}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        field.onChange(Number(event.target.value))
                      }
                      }
                    />
                  )}
                />

                <button className="mt-7 ms-2 btn btn-sm btn-light-danger p-2 m-1" onClick={() => remove(index)} data-bs-toggle="tooltip" data-bs-placement="top" title={'Delete'.toTranslatedDisplay()}>
                  <KTIcon className="fs-3 text-muted text-hover-danger" iconName='trash' />
                </button>

              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-light-primary my-2"
              onClick={() => append({ taxName: '', taxPct: 0 })}
            >
              {'Add Sub Tax'.toTranslatedDisplay()}
            </button>
          </div>
        </>
      ) : (
        <div className='mb-3'>

          <AccountPicker tabIndex={0}
            label={'Tax Account'.toTranslatedDisplay()}
            selectedAccounts={[watchTaxAcct || '']}
            sysAcctCode={SystemAccountCode.STE}
            onAccountChange={function (accountId: string | string[]): void {
              if (Array.isArray(accountId)) {
                // Assuming you want to set the first value from the array
                setValue('taxAcct', accountId[0]);
              } else {
                setValue('taxAcct', accountId);
              }
            }} />
          <div className='mb-3'></div>
          <AccountPicker tabIndex={0}
            label={'Tax Payable Account'.toTranslatedDisplay()}
            selectedAccounts={[watchTaxPayableAcct || '']}
            sysAcctCode={SystemAccountCode.STP}
            accountCategory={AccountCategory.CurrentLiabilities}
            onAccountChange={function (accountId: string | string[]): void {
              if (Array.isArray(accountId)) {
                // Assuming you want to set the first value from the array
                setValue('taxPayableAcct', accountId[0]);
              } else {
                setValue('taxPayableAcct', accountId);
              }
            }} />
          {isCIN &&
            <div className="mt-3">
              <label className="form-label">{'Inter/Intra Tax'.toTranslatedDisplay()}</label>
              <div className="d-flex align-items-center mt-1">
                <Controller
                  name="intraTax" // This field will control intraTax
                  control={control}
                  render={({ field }) => (
                    <>
                      <div className="form-check me-3">
                        <input
                          className="form-check-input"
                          type="radio"
                          value="true"
                          checked={field.value === true}
                          onChange={() => {
                            field.onChange(true);
                            setValue('interTax', false); // Set interTax to false when intraTax is true
                          }}
                        />
                        <label className="form-check-label">{'Intra'.toTranslatedDisplay()}</label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          value="false"
                          checked={field.value === false}
                          onChange={() => {
                            field.onChange(false);
                            setValue('interTax', true); // Set interTax to true when intraTax is false
                          }}
                        />
                        <label className="form-check-label">{'Inter'.toTranslatedDisplay()}</label>
                      </div>
                    </>
                  )}
                />
              </div>
            </div>
          }

        </div>
      )}
      <button type="submit" className="btn btn-primary">{'Submit'.toTranslatedDisplay()}</button>
    </form>
  );
};

